import type { BuildAndBuyDispatchType } from "../../../redux/store";
import { useBnbSelector } from "../../../redux/store";
import { lexusAemNavStepsOrder, NavStep, navStepsOrder } from "../../../../shared-logic/utils/Navigation";
import { setStep } from "../../../redux/actions/NavigationActions";
import useStepUrl from "./useStepUrl";
import type { BuildAndBuyLabelType } from "../../../utils/labels";
import { nextLabels, useBnbOptionalLabel } from "../../../utils/labels";
import { showServicesStep } from "../../../utils/settings/bnbSettingUtils";
import { isLexus } from "../../../../../../common-deprecated/settings/utils/commonSettingUtils";
import { canNavigateToBuyStep } from "../../../../../shared-logic/utils/checkoutUtils";
import type { FinanceRateStateType } from "../../../redux/reducers/FinanceRateReducer";
import { useDispatch } from "../../../../../../shared-logic/redux/store";

type StepLinkType = {
    url: string; // full link to a specific step
    go: (event: React.SyntheticEvent) => void; // helper to navigate to the step as SPA
    label: string | null;
    step: NavStep;
};

// These labels should have a ...Monthly variant as well
const prevLabels: Partial<Record<NavStep, BuildAndBuyLabelType>> = {
    [NavStep.Model]: "stepBackToModelEngine",
    [NavStep.Engine]: "stepBackToModelEngine",
    [NavStep.Grades]: "stepBackToGrade",
    [NavStep.Customize]: "stepBackToCustomize",
    [NavStep.Services]: "stepBackToServices",
};

/**
 * Helper for navigating between navSteps.
 * @returns An object containing the direct url (SEO / accessibility reasons) and a helper function to navigate there in a SPA like fashion.
 * @param direction
 * @param ignoreLabels
 * target step to which the link should point
 */

const useSiblingStepLink = (direction: "next" | "prev", ignoreLabels?: boolean): StepLinkType | null => {
    const dispatch = useDispatch<BuildAndBuyDispatchType>();
    const currentStep = useBnbSelector((state) => state.navigation.step);
    const finance = useBnbSelector((state) => state.finance);
    const financeOption = useBnbSelector((state) => state.utils.financeOption);
    const bnbSettings = useBnbSelector((state) => state.bnbSettings);
    const commonSettings = useBnbSelector((state) => state.commonSettings);
    const car = useBnbSelector((state) => state.car);
    const basket = useBnbSelector((state) => state.basket);
    const usedNavStepsOrder = isLexus(commonSettings) ? lexusAemNavStepsOrder : navStepsOrder;
    let customNavProps;

    const currentIndex = usedNavStepsOrder.indexOf(currentStep);
    const isNext = direction === "next";
    const nextIndex = currentIndex + (isNext ? 1 : -1);

    let target = usedNavStepsOrder[nextIndex];

    // Skip services on Build V1 or when services step is disabled
    if (!showServicesStep(bnbSettings, commonSettings) && target === NavStep.Services) {
        target = isNext ? NavStep.Summary : NavStep.Customize;
    }

    if (target === NavStep.Grades) customNavProps = { subModelId: car.submodel?.id };

    const { href: url, onClick } = useStepUrl(target, customNavProps);

    const nextLabel = useBnbOptionalLabel(`${nextLabels[target] || ""}` as BuildAndBuyLabelType);
    const prevLabel = useBnbOptionalLabel(`${prevLabels[target] || ""}` as BuildAndBuyLabelType);
    const nextMonthlyLabel = useBnbOptionalLabel(`${nextLabels[target] || ""}Monthly` as BuildAndBuyLabelType);

    let label = isNext ? nextLabel : prevLabel;

    if (isNext && financeOption === "monthly" && nextMonthlyLabel) {
        label = nextMonthlyLabel;
    }

    // Clear label so it won't be shown when there is no buy step enabled
    if (
        (!label && !ignoreLabels) ||
        (isNext &&
            target === NavStep.Buy &&
            !canNavigateToBuyStep<FinanceRateStateType>(
                bnbSettings.enableBuy,
                bnbSettings.allowedCheckoutFinanceProduct,
                finance,
                financeOption,
                basket,
            ))
    ) {
        return null;
    }

    return {
        url,
        go: (event: React.SyntheticEvent) => {
            if (target === NavStep.Buy) {
                event.preventDefault();
                dispatch(setStep(target));
            } else onClick(event);
        },
        label,
        step: target,
    };
};

export default useSiblingStepLink;
